import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { useQuery } from '@tanstack/react-query'
import { CmApiHttpError, cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import {
  analyticsTransfersPageFromLive,
  emptyCmTransferResult,
  toCmTransferResult,
  type CmTransferResult,
} from '@/api/map/memberTransfers'

export type MemberTransfersQuery = {
  agentId: string
  from: string
  to: string
  page?: number
  pageSize?: number
  currencyType?: string
  /** Only rows where this seat is payer or payee (Agent Member tab default). */
  partyOnly?: boolean
  enabled?: boolean
}

const UPSTREAM_PAGE_SIZE = 100
const MAX_PAGES = 50

async function fetchAllPages(
  agentId: string,
  from: string,
  to: string,
): Promise<ReturnType<typeof analyticsTransfersPageFromLive>[]> {
  const pages = []
  for (let page = 0; page < MAX_PAGES; page++) {
    const live = await cmApi.memberTransfers(authHeaders(), agentId, {
      from,
      to,
      page,
      size: UPSTREAM_PAGE_SIZE,
    })
    const mapped = analyticsTransfersPageFromLive(live)
    pages.push(mapped)
    const got = mapped.rows?.length ?? 0
    const total = mapped.total
    if (got < UPSTREAM_PAGE_SIZE) break
    if (typeof total === 'number' && (page + 1) * UPSTREAM_PAGE_SIZE >= total) break
  }
  return pages
}

/** Full-range load for Account Statement Generate / CSV (all pages, then optional slice). */
export async function loadMemberTransfers(
  query: MemberTransfersQuery,
): Promise<CmTransferResult> {
  return load(query)
}

async function load(query: MemberTransfersQuery): Promise<CmTransferResult> {
  const page = Math.max(1, query.page ?? 1)
  const pageSize = Math.max(1, query.pageSize ?? DEFAULT_PAGE_SIZE)
  const id = query.agentId
  if (!id || !query.from || !query.to) return emptyCmTransferResult(page, pageSize)

  try {
    const pages = await fetchAllPages(id, query.from, query.to)
    const mapped = toCmTransferResult(pages, id, query.currencyType)
    const partyOnly = query.partyOnly ?? true
    const filtered = partyOnly
      ? mapped.rows.filter((t) => t.fromId === id || t.toId === id)
      : mapped.rows

    const totals = partyOnly
      ? filtered.reduce(
          (acc, t) => {
            if (t.fromId === id) acc.given += t.amount
            else if (t.toId === id) acc.taken += t.amount
            acc.count += 1
            return acc
          },
          { given: 0, taken: 0, net: 0, count: 0 },
        )
      : mapped.totals
    if (partyOnly) {
      totals.given = Math.round(totals.given * 100) / 100
      totals.taken = Math.round(totals.taken * 100) / 100
      totals.net = Math.round((totals.taken - totals.given) * 100) / 100
    }

    const start = (page - 1) * pageSize
    return {
      rows: filtered.slice(start, start + pageSize),
      total: filtered.length,
      page,
      pageSize,
      totals,
      asOf: mapped.asOf,
      martStale: mapped.martStale,
    }
  } catch (err) {
    if (err instanceof CmApiHttpError && (err.status === 404 || err.status === 503)) {
      return emptyCmTransferResult(page, pageSize)
    }
    return emptyCmTransferResult(page, pageSize)
  }
}

export function useMemberTransfers(query: MemberTransfersQuery) {
  return useQuery({
    queryKey: ['cm', 'member-transfers', query],
    queryFn: () => load(query),
    enabled: Boolean(query.agentId && query.from && query.to && (query.enabled ?? true)),
    placeholderData: (prev) => prev,
  })
}
